import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { FileText, FileSpreadsheet, Loader2, Download } from 'lucide-react';
import { format } from 'date-fns';
import { currentDateFnsLocale, currentLanguage } from '@/i18n/locale';
import { toast } from 'sonner';
import { hasMeetingBooked } from '@/utils/callUtils';
import { toCsv, downloadCsv } from '@/lib/exportCsv';

interface CallRecord {
  created_date?: string | Date | null;
  prospect_name?: string | null;
  prospect_company?: string | null;
  prospect_phone?: string | null;
  agent_name?: string | null;
  agent_email?: string | null;
  call_status?: string | null;
  call_outcome?: string | null;
  duration_seconds?: number | null;
  source?: string | null;
  call_notes?: string | null;
  meeting_booked?: boolean | null;
}

interface AgentRecord {
  email?: string;
  full_name?: string;
}

interface ProspectRecord {
  id?: string;
}

interface SourceCompletionEntry {
  called: number;
  total: number;
  pct: number;
}

interface ExportReportsProps {
  /** Calls of the selection, counted by the server. */
  callCount: number;
  /**
   * Every call of the selection, read page by page when an export starts
   * (W4, T-d3): the export used to write the 500 calls the browser had loaded.
   */
  loadCalls: () => Promise<CallRecord[]>;
  agents?: AgentRecord[];
  prospectsBySource?: Record<string, ProspectRecord[]>;
  sourceCompletion?: Record<string, SourceCompletionEntry>;
  activeSource?: string;
  analyticsPeriod?: string;
}

function getPeriodLabels(t: (key: string) => string): Record<string, string> {
  return {
    today: t('pages:analytics.exportReports.periodLabels.today'),
    last_7_days: t('pages:analytics.exportReports.periodLabels.last7Days'),
    last_14_days: t('pages:analytics.exportReports.periodLabels.last14Days'),
    last_30_days: t('pages:analytics.exportReports.periodLabels.last30Days'),
    last_6_months: t('pages:analytics.exportReports.periodLabels.last6Months'),
    last_12_months: t('pages:analytics.exportReports.periodLabels.last12Months'),
    custom: t('pages:analytics.exportReports.periodLabels.custom'),
  };
}

/**
 * ExportReports: tickets 9.9 (CSV + PDF export from Analytics).
 */
export default function ExportReports({
  callCount,
  loadCalls,
  agents = [],
  prospectsBySource = {},
  sourceCompletion = {},
  activeSource = 'all',
  analyticsPeriod = '',
}: ExportReportsProps) {
  const { t } = useTranslation(['pages', 'common']);
  const PERIOD_LABELS = getPeriodLabels(t);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  // ── CSV export ─────────────────────────────────────────────────────────────
  const exportToCSV = async () => {
    setExportingCsv(true);
    try {
      const calls = await loadCalls();
      const headers = t('pages:analytics.exportReports.csv.headers');

      const rows = calls.map((call) => {
        let dateStr = '';
        try {
          if (call.created_date) {
            dateStr = format(new Date(call.created_date), 'dd/MM/yyyy HH:mm', { locale: currentDateFnsLocale() });
          }
        } catch {
          dateStr = typeof call.created_date === 'string' ? call.created_date : '';
        }
        return [
          dateStr,
          call.prospect_name || '',
          call.prospect_company || '',
          call.prospect_phone || '',
          call.agent_name || '',
          call.call_status || '',
          call.call_outcome || '',
          call.duration_seconds || 0,
          call.source || '',
          call.call_notes || '',
        ];
      });

      // Append a completion-per-list summary block at the bottom
      const summaryRows = Object.entries(sourceCompletion)
        .filter(([key]) => key !== '__all')
        .map(([src, s]) => [src, s.called, s.total, `${s.pct}%`]);
      const allComp = sourceCompletion.__all || { called: 0, total: 0, pct: 0 };
      summaryRows.push([t('pages:analytics.exportReports.csv.summaryTotal'), allComp.called, allComp.total, `${allComp.pct}%`]);

      const csv = [
        toCsv(headers.split(','), rows),
        '',
        toCsv([t('pages:analytics.exportReports.csv.summaryTitle')], []),
        toCsv(t('pages:analytics.exportReports.csv.summaryHeaders').split(','), summaryRows),
      ].join('\n');
      downloadCsv(`rapport_appels_${format(new Date(), 'yyyy-MM-dd')}.csv`, csv);

      toast.success(t('pages:analytics.exportReports.csv.exportedSuccess', { count: calls.length }));
    } catch (error) {
      toast.error(t('pages:analytics.exportReports.csv.exportError'));
      console.error(error);
    } finally {
      setExportingCsv(false);
    }
  };

  // ── PDF export ─────────────────────────────────────────────────────────────
  const exportToPDF = async () => {
    setExportingPdf(true);
    try {
      const calls = await loadCalls();
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      const PAGE_W = 210;
      const MARGIN = 18;
      const CONTENT_W = PAGE_W - MARGIN * 2;
      let y = MARGIN;

      // ── Helper: add a new page if needed ──
      const checkPage = (needed: number = 8) => {
        if (y + needed > 280) {
          doc.addPage();
          y = MARGIN;
        }
      };

      // ── Helper: section title ──
      const sectionTitle = (text: string) => {
        checkPage(14);
        y += 4;
        doc.setFillColor(240, 244, 255);
        doc.rect(MARGIN, y - 4, CONTENT_W, 9, 'F');
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 64, 175);
        doc.text(text, MARGIN + 2, y + 1.5);
        doc.setTextColor(30, 30, 30);
        y += 9;
      };

      // ── Helper: key-value row ──
      const kvRow = (label: string, value: string | number, indent: number = 0) => {
        checkPage(7);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(label, MARGIN + indent, y);
        doc.setFont('helvetica', 'normal');
        doc.text(String(value), MARGIN + indent + 42, y);
        y += 6;
      };

      // ── Cover / header ──────────────────────────────────────────────────────
      doc.setFillColor(30, 64, 175);
      doc.rect(0, 0, PAGE_W, 28, 'F');
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text(t('pages:analytics.exportReports.pdf.reportTitle'), MARGIN, 14);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(
        t('pages:analytics.exportReports.pdf.generatedOn', {
          date: format(new Date(), currentLanguage() === 'en' ? "MMMM d, yyyy 'at' HH:mm" : 'dd MMMM yyyy à HH:mm', {
            locale: currentDateFnsLocale(),
          }),
        }),
        MARGIN,
        21,
      );
      doc.setTextColor(30, 30, 30);
      y = 36;

      // ── Metadata ──────────────────────────────────────────────────────────
      sectionTitle(t('pages:analytics.exportReports.pdf.reportSettings'));
      kvRow(t('pages:analytics.exportReports.pdf.period'), PERIOD_LABELS[analyticsPeriod] || analyticsPeriod);
      kvRow(t('pages:analytics.exportReports.pdf.listFilter'), activeSource === 'all' ? t('pages:analytics.exportReports.pdf.allLists') : activeSource);
      kvRow(t('pages:analytics.exportReports.pdf.callsIncluded'), calls.length);

      // ── Summary KPIs ──────────────────────────────────────────────────────
      const totalCalls = calls.length;
      const connected = calls.filter((c) =>
        c.call_outcome != null &&
        ['contacted', 'meeting_booked', 'connected', 'connected_to_agent'].includes(c.call_outcome),
      ).length;
      const meetings = calls.filter(hasMeetingBooked).length;
      const noAnswer = calls.filter(c => c.call_outcome === 'no_answer').length;
      const voicemail = calls.filter(c => c.call_outcome === 'voicemail').length;
      // #1: « iOS Filter present dans les stats ». Il n'etait compte dans
      // aucun KPI : ni connected, ni noAnswer, ni voicemail.
      const iosFilter = calls.filter(c => c.call_outcome === 'ios_filter').length;
      const connectionRate = totalCalls > 0 ? ((connected / totalCalls) * 100).toFixed(1) : '0.0';
      const avgDuration = totalCalls > 0
        ? Math.round(calls.reduce((a, c) => a + (c.duration_seconds || 0), 0) / totalCalls)
        : 0;

      sectionTitle(t('pages:analytics.exportReports.pdf.callsSummary'));
      kvRow(t('pages:analytics.exportReports.pdf.totalCalls'), totalCalls);
      kvRow(t('pages:analytics.exportReports.pdf.connectedContacts'), `${connected} (${connectionRate}%)`);
      kvRow(t('pages:analytics.exportReports.pdf.meetingsBooked'), meetings);
      kvRow(t('pages:analytics.exportReports.pdf.noAnswer'), noAnswer);
      kvRow(t('pages:analytics.exportReports.pdf.voicemail'), voicemail);
      kvRow(t('pages:analytics.exportReports.pdf.iosFilter'), iosFilter);
      kvRow(t('pages:analytics.exportReports.pdf.avgDuration'), `${avgDuration}s`);

      // ── Completion per list (ticket 9.4) ───────────────────────────────────
      const listEntries = Object.entries(sourceCompletion).filter(([k]) => k !== '__all');
      if (listEntries.length > 0) {
        sectionTitle(t('pages:analytics.exportReports.pdf.listConsumption'));

        // Column headers
        checkPage(8);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setFillColor(226, 232, 240);
        doc.rect(MARGIN, y - 3, CONTENT_W, 7, 'F');
        doc.text(t('pages:analytics.exportReports.pdf.columns.list'), MARGIN + 2, y + 1);
        doc.text(t('pages:analytics.exportReports.pdf.columns.called'), MARGIN + 80, y + 1);
        doc.text(t('pages:analytics.exportReports.pdf.columns.total'), MARGIN + 105, y + 1);
        doc.text(t('pages:analytics.exportReports.pdf.columns.consumedPct'), MARGIN + 128, y + 1);
        doc.text(t('pages:analytics.exportReports.pdf.columns.remaining'), MARGIN + 162, y + 1);
        y += 9;

        doc.setFont('helvetica', 'normal');
        listEntries.forEach(([src, comp], i) => {
          checkPage(7);
          if (i % 2 === 0) {
            doc.setFillColor(248, 250, 252);
            doc.rect(MARGIN, y - 3, CONTENT_W, 6.5, 'F');
          }
          doc.setFontSize(8);
          // Truncate long source names
          const truncSrc = src.length > 38 ? src.slice(0, 35) + '...' : src;
          doc.text(truncSrc, MARGIN + 2, y + 1);
          doc.text(String(comp.called), MARGIN + 80, y + 1);
          doc.text(String(comp.total), MARGIN + 105, y + 1);

          // Progress bar
          const barX = MARGIN + 128;
          const barW = 30;
          const barH = 3;
          const barY = y - 0.5;
          doc.setFillColor(226, 232, 240);
          doc.roundedRect(barX, barY, barW, barH, 1, 1, 'F');
          if (comp.pct > 0) {
            doc.setFillColor(59, 130, 246);
            doc.roundedRect(barX, barY, Math.max(1, barW * comp.pct / 100), barH, 1, 1, 'F');
          }
          doc.setTextColor(30, 30, 30);
          doc.text(`${comp.pct}%`, barX + barW + 2, y + 1);
          doc.text(String(comp.total - comp.called), MARGIN + 162, y + 1);
          y += 6.5;
        });

        // Total row
        const allComp = sourceCompletion.__all || { called: 0, total: 0, pct: 0 };
        checkPage(8);
        doc.setFillColor(219, 234, 254);
        doc.rect(MARGIN, y - 3, CONTENT_W, 7, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.text(t('pages:analytics.exportReports.pdf.total'), MARGIN + 2, y + 1);
        doc.text(String(allComp.called), MARGIN + 80, y + 1);
        doc.text(String(allComp.total), MARGIN + 105, y + 1);
        doc.text(`${allComp.pct}%`, MARGIN + 128, y + 1);
        doc.text(String(allComp.total - allComp.called), MARGIN + 162, y + 1);
        y += 9;
      }

      // ── Agent performance ─────────────────────────────────────────────────
      const agentsWithCalls = agents.filter(agent =>
        calls.some(c => c.agent_email === agent.email)
      );
      if (agentsWithCalls.length > 0) {
        sectionTitle(t('pages:analytics.exportReports.pdf.agentPerformance'));

        checkPage(8);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setFillColor(226, 232, 240);
        doc.rect(MARGIN, y - 3, CONTENT_W, 7, 'F');
        doc.text(t('pages:analytics.exportReports.pdf.columns.agent'), MARGIN + 2, y + 1);
        doc.text(t('pages:analytics.exportReports.pdf.columns.calls'), MARGIN + 80, y + 1);
        doc.text(t('pages:analytics.exportReports.pdf.columns.connected'), MARGIN + 102, y + 1);
        doc.text(t('pages:analytics.exportReports.pdf.columns.rate'), MARGIN + 128, y + 1);
        doc.text(t('pages:analytics.exportReports.pdf.columns.meetings'), MARGIN + 152, y + 1);
        y += 9;

        doc.setFont('helvetica', 'normal');
        agentsWithCalls.slice(0, 20).forEach((agent, i) => {
          const agentCalls = calls.filter(c => c.agent_email === agent.email);
          const agentConnected = agentCalls.filter((c) =>
            c.call_outcome != null &&
            ['contacted', 'meeting_booked', 'connected'].includes(c.call_outcome),
          ).length;
          const agentMeetings = agentCalls.filter(hasMeetingBooked).length;
          const agentRate = agentCalls.length > 0
            ? ((agentConnected / agentCalls.length) * 100).toFixed(1)
            : '0.0';

          checkPage(7);
          if (i % 2 === 0) {
            doc.setFillColor(248, 250, 252);
            doc.rect(MARGIN, y - 3, CONTENT_W, 6.5, 'F');
          }
          doc.setFontSize(8);
          const agentName = agent.full_name || agent.email || '';
          const truncName = agentName.length > 38 ? agentName.slice(0, 35) + '...' : agentName;
          doc.text(truncName, MARGIN + 2, y + 1);
          doc.text(String(agentCalls.length), MARGIN + 80, y + 1);
          doc.text(String(agentConnected), MARGIN + 102, y + 1);
          doc.text(`${agentRate}%`, MARGIN + 128, y + 1);
          doc.text(String(agentMeetings), MARGIN + 152, y + 1);
          y += 6.5;
        });
        y += 4;
      }

      // ── Recent call details (last 100) ────────────────────────────────────
      if (calls.length > 0) {
        sectionTitle(t('pages:analytics.exportReports.pdf.recentCallDetails', { shown: Math.min(calls.length, 100), total: calls.length }));

        checkPage(8);
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'bold');
        doc.setFillColor(226, 232, 240);
        doc.rect(MARGIN, y - 3, CONTENT_W, 7, 'F');
        doc.text(t('pages:analytics.exportReports.pdf.columns.date'), MARGIN + 2, y + 1);
        doc.text(t('pages:analytics.exportReports.pdf.columns.prospect'), MARGIN + 28, y + 1);
        doc.text(t('pages:analytics.exportReports.pdf.columns.company'), MARGIN + 78, y + 1);
        doc.text(t('pages:analytics.exportReports.pdf.columns.outcome'), MARGIN + 124, y + 1);
        doc.text(t('pages:analytics.exportReports.pdf.columns.duration'), MARGIN + 158, y + 1);
        y += 9;

        doc.setFont('helvetica', 'normal');
        calls.slice(0, 100).forEach((call, i) => {
          checkPage(6);
          if (i % 2 === 0) {
            doc.setFillColor(248, 250, 252);
            doc.rect(MARGIN, y - 3, CONTENT_W, 6, 'F');
          }
          const noValue = t('common:states.noValue');
          let callDate = noValue;
          try {
            if (call.created_date) callDate = format(new Date(call.created_date), 'dd/MM HH:mm');
          } catch {
            callDate = noValue;
          }
          const name = (call.prospect_name || noValue).slice(0, 22);
          const company = (call.prospect_company || noValue).slice(0, 22);
          const outcome = (call.call_outcome || noValue).slice(0, 18);
          const dur = call.duration_seconds ? `${call.duration_seconds}s` : noValue;

          doc.setFontSize(7.5);
          doc.text(callDate, MARGIN + 2, y + 1);
          doc.text(name, MARGIN + 28, y + 1);
          doc.text(company, MARGIN + 78, y + 1);
          doc.text(outcome, MARGIN + 124, y + 1);
          doc.text(dur, MARGIN + 158, y + 1);
          y += 6;
        });
      }

      // ── Footer on every page ───────────────────────────────────────────────
      const pageCount = doc.getNumberOfPages();
      for (let p = 1; p <= pageCount; p++) {
        doc.setPage(p);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(150, 150, 150);
        doc.text(
          t('pages:analytics.exportReports.pdf.footer', {
            date: format(new Date(), currentLanguage() === 'en' ? "MM/dd/yyyy 'at' HH:mm" : 'dd/MM/yyyy à HH:mm', {
              locale: currentDateFnsLocale(),
            }),
          }),
          MARGIN,
          291,
        );
        doc.text(t('pages:analytics.exportReports.pdf.pageOf', { page: p, total: pageCount }), PAGE_W - MARGIN - 18, 291);
      }

      doc.save(`rapport_appels_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
      toast.success(t('pages:analytics.exportReports.pdf.exportedSuccess'));
    } catch (error) {
      toast.error(t('pages:analytics.exportReports.pdf.exportError'));
      console.error(error);
    } finally {
      setExportingPdf(false);
    }
  };

  const exporting = exportingCsv || exportingPdf;

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h2 className="text-base font-medium text-foreground mb-1">{t('pages:analytics.exportReports.title')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('pages:analytics.exportReports.callsInSelection', { count: callCount, plural: callCount !== 1 ? 's' : '' })}
            {activeSource !== 'all' ? t('pages:analytics.exportReports.listSuffix', { source: activeSource }) : ''}
          </p>
        </div>
        <Download className="w-4 h-4 text-muted-foreground mt-0.5" />
      </div>

      {/* Completion summary (ticket 9.4) */}
      {Object.keys(sourceCompletion).filter(k => k !== '__all').length > 0 && (
        <div className="mb-5 grid grid-cols-1 gap-2">
          {Object.entries(sourceCompletion)
            .filter(([k]) => k !== '__all')
            .map(([src, comp]) => (
              <div key={src} className="flex items-center gap-3">
                <span className="text-xs text-foreground/70 w-36 truncate flex-shrink-0" title={src}>{src}</span>
                <div className="flex-1 bg-muted rounded-full h-1.5 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${comp.pct}%`, background: 'var(--vf-text-4)' }}
                  />
                </div>
                <span className="text-xs tabular-nums text-muted-foreground whitespace-nowrap w-28 text-right">
                  {t('pages:analytics.exportReports.calledOfTotal', { called: comp.called, total: comp.total, pct: comp.pct })}
                </span>
              </div>
            ))}
          {/* Total row */}
          {sourceCompletion.__all && sourceCompletion.__all.total > 0 && (
            <div className="flex items-center gap-3 pt-1 border-t border-border mt-1">
              <span className="text-xs font-semibold text-foreground w-36 flex-shrink-0">{t('pages:analytics.exportReports.total')}</span>
              <div className="flex-1 bg-muted rounded-full h-1.5 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${sourceCompletion.__all.pct}%`, background: 'var(--vf-accent)' }}
                />
              </div>
              <span className="text-xs tabular-nums font-semibold text-foreground whitespace-nowrap w-28 text-right">
                {t('pages:analytics.exportReports.calledOfTotal', {
                  called: sourceCompletion.__all.called,
                  total: sourceCompletion.__all.total,
                  pct: sourceCompletion.__all.pct,
                })}
              </span>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-3">
        <Button
          onClick={exportToCSV}
          disabled={exporting || callCount === 0}
          variant="outline"
          className="flex-1 h-11 border-border hover:bg-accent/50"
        >
          {exportingCsv ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t('pages:analytics.exportReports.exportingCsv')}
            </>
          ) : (
            <>
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              CSV
            </>
          )}
        </Button>
        <Button
          onClick={exportToPDF}
          disabled={exporting || callCount === 0}
          variant="outline"
          className="flex-1 h-11 border-border hover:bg-accent/50"
        >
          {exportingPdf ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t('pages:analytics.exportReports.exportingPdf')}
            </>
          ) : (
            <>
              <FileText className="w-4 h-4 mr-2" />
              PDF
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
